import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  getSegments,
  createSegment,
  createSegmentFromPoll,
  updateSegment,
  deleteSegment,
} from '../api/push-notification';
import { useUserAuth } from '../contexts/auth';
import { CreateSegmentFromPollBody } from '../types/segment';

export function useSegmentMutation({ abbrv, onSuccess }: { abbrv: string; onSuccess: () => void }) {
  const { currentUser } = useUserAuth();
  return useMutation({
    mutationFn: async (payload: {
      body?: any | FormData;
      id?: string;
      action: string;
      isFormData?: boolean;
    }) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      if (payload.action === 'create') {
        const response = await createSegment(abbrv, payload.body, auth);
        return response;
      }
      if (payload.action === 'edit' && payload?.id) {
        const response = await updateSegment(payload.id, payload.body, auth);
        return response;
      }
      if (payload.action === 'delete' && payload?.id) {
        const response = await deleteSegment(payload.id, auth);
        return response;
      }
    },
    onSuccess() {
      onSuccess();
    },
  });
}

export function useSegments(abbrv: string) {
  const { currentUser } = useUserAuth();
  const query = useQuery({
    queryKey: ['segments', abbrv],
    queryFn: async () => {
      const auth = await currentUser?.getIdToken();
      if (!abbrv || !auth) {
        return [];
      }
      const segments = await getSegments(abbrv, auth);

      return segments;
    },
    refetchOnWindowFocus: false,
  });
  return {
    query,
    mutation: useSegmentMutation({
      abbrv,
      onSuccess: () => {
        query.refetch();
      },
    }),
  };
}

/** Creates a push segment from the participants or winners of a poll or quiz. */
export function useCreateSegmentFromPoll(abbrv: string) {
  const { currentUser } = useUserAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: CreateSegmentFromPollBody) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        throw new Error('Not signed in');
      }

      return createSegmentFromPoll(abbrv, body, auth);
    },
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['segments', abbrv] });
    },
  });
}
